import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { Badge } from "../../app/components/ui/badge";
import { useAuth } from "../../providers/AuthProvider";
import { Alert, AlertDescription, AlertTitle } from "../../app/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/ui/dialog";
import { Label } from "../../app/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../app/components/ui/select";
import { Textarea } from "../../app/components/ui/textarea";
import { TableLayout, type TableColumn } from "../../components/dashboard";

type Row = {
  id: string;
  name: string;
  status: "pending" | "verified" | "rejected";
  ownerUserId: string;
  ownerEmail: string;
  ownerName: string;
};

function badgeVariant(status: Row["status"]) {
  if (status === "verified") return "default";
  if (status === "rejected") return "destructive";
  return "secondary";
}

export function BusinessVerificationPage() {
  const { token } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Row["status"]>("pending");

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setError(null);
    setIsLoading(true);
    try {
      const list = await api.admin.getBusinesses({ token, status: statusFilter });
      setRows(list as Row[]);
    } catch (err) {
      setError((err as { message?: string })?.message ?? "Failed to load businesses");
    } finally {
      setIsLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const pendingCount = useMemo(() => rows.length, [rows.length]);

  const approve = useCallback(
    async (id: string) => {
      if (!token) return;
      setBusyId(id);
      setError(null);
      try {
        await api.admin.verifyBusiness({ token, id });
        await load();
      } catch (err) {
        setError((err as { message?: string })?.message ?? "Failed to update business");
      } finally {
        setBusyId(null);
      }
    },
    [token, load],
  );

  const openReject = useCallback((id: string) => {
    setRejectTargetId(id);
    setRejectReason("");
    setRejectOpen(true);
  }, []);

  const submitReject = useCallback(async () => {
    if (!token || !rejectTargetId) return;
    const reason = rejectReason.trim();
    if (!reason) return;

    setBusyId(rejectTargetId);
    setError(null);
    try {
      await api.admin.rejectBusiness({ token, id: rejectTargetId, reason });
      setRejectOpen(false);
      setRejectTargetId(null);
      await load();
    } catch (err) {
      setError((err as { message?: string })?.message ?? "Failed to reject business");
    } finally {
      setBusyId(null);
    }
  }, [token, rejectTargetId, rejectReason, load]);

  const columns: TableColumn<Row>[] = useMemo(
    () => [
      { key: "business", header: "Business", render: (r) => <span className="font-medium">{r.name}</span> },
      {
        key: "owner",
        header: "Owner",
        render: (r) => (
          <div className="leading-tight">
            <div className="font-medium">{r.ownerName}</div>
            <div className="text-sm text-muted-foreground">{r.ownerEmail}</div>
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (r) => (
          <Badge variant={badgeVariant(r.status) as "default" | "secondary" | "destructive"}>{r.status}</Badge>
        ),
      },
      {
        key: "actions",
        header: "Actions",
        render: (r) => (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="min-h-9"
              onClick={() => void approve(r.id)}
              disabled={busyId === r.id || r.status !== "pending"}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="min-h-9"
              onClick={() => openReject(r.id)}
              disabled={busyId === r.id || r.status !== "pending"}
            >
              Reject
            </Button>
          </div>
        ),
      },
    ],
    [approve, busyId, openReject],
  );

  const mobileCardRender = useCallback(
    (r: Row) => (
      <div className="space-y-3">
        <div className="font-medium">{r.name}</div>
        <div className="text-sm text-muted-foreground">
          {r.ownerName} · {r.ownerEmail}
        </div>
        <Badge variant={badgeVariant(r.status) as "default" | "secondary" | "destructive"}>{r.status}</Badge>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            size="sm"
            className="min-h-10 flex-1"
            onClick={() => void approve(r.id)}
            disabled={busyId === r.id || r.status !== "pending"}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="min-h-10 flex-1"
            onClick={() => openReject(r.id)}
            disabled={busyId === r.id || r.status !== "pending"}
          >
            Reject
          </Button>
        </div>
      </div>
    ),
    [approve, busyId, openReject],
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Business verification</h1>
        <p className="text-muted-foreground">Review and approve pending businesses.</p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div>
              <CardTitle>
                {statusFilter === "pending"
                  ? "Pending businesses"
                  : statusFilter === "verified"
                    ? "Verified businesses"
                    : "Rejected businesses"}
              </CardTitle>
              <CardDescription>
                {pendingCount}{" "}
                {statusFilter === "pending"
                  ? "awaiting review"
                  : statusFilter === "verified"
                    ? "approved"
                    : "rejected"}
              </CardDescription>
            </div>

            <div className="min-w-0 w-full sm:w-auto sm:min-w-[180px]">
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as Row["status"])}>
                <SelectTrigger className="min-h-9 w-full sm:w-[180px]">
                  <SelectValue placeholder="Status filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No businesses for this filter.</div>
          ) : (
            <div className="min-w-0 rounded-md border md:border-0">
              <TableLayout columns={columns} rows={rows} mobileCardRender={mobileCardRender} />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={rejectOpen}
        onOpenChange={(v) => {
          setRejectOpen(v);
          if (!v) {
            setRejectTargetId(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject business</DialogTitle>
            <DialogDescription>
              Provide a rejection reason. This will be shown to the business owner.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="rejection_reason">Rejection reason</Label>
            <Textarea
              id="rejection_reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why the business was rejected..."
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectOpen(false);
                setRejectTargetId(null);
                setRejectReason("");
              }}
              disabled={busyId !== null}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void submitReject()}
              disabled={!rejectTargetId || busyId === rejectTargetId || rejectReason.trim().length === 0}
            >
              {busyId === rejectTargetId ? "Rejecting..." : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
